import { useState } from "react";
import { Card, Row, Col, Badge } from "react-bootstrap";
import { CommonButton, CommonInput, CommonSelect } from "../Input";
import CommonDatePicker from "../Input/CommonDatePicker";
import DataTable from "../table/DataTable";
import BeneficiaryDetailsModal from "./BeneficiaryDetailsModal";
import beneficiariesData from "./data";

const Beneficiaries = () => {
  // Filter states
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [userId, setUserId] = useState("");
  const [bnfName, setBnfName] = useState("");
  const [receiverType, setReceiverType] = useState(null);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);

  const [filteredData, setFilteredData] = useState(beneficiariesData);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Get current items
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const receiverTypeOptions = [
    { value: "Individual", label: "Individual" },
    { value: "Organization", label: "Organization" },
  ];

  const columns = [
    { label: "User ID", key: "userId", className: "ps-4" },
    {
      label: "User name",
      key: "userName",
      className: "ps-2",
      render: (item) => <div className="text-black">{item.userName}</div>,
    },
    { label: "Beneficiary ID", key: "beneficiaryId", className: "ps-4" },
    {
      label: "Beneficiary name",
      key: "beneficiaryName",
      className: "ps-2",
      render: (item) => (
        <div className="text-black">{item.beneficiaryName}</div>
      ),
    },
    { label: "Type", key: "receiverType", className: "ps-4" },
    { label: "Receiving method", key: "receivingMethod", className: "ps-4" },
    { label: "Phone number", key: "phone", className: "ps-4" },
    { label: "Created time", key: "createdTime", className: "ps-2" },
    {
      label: "Action",
      key: "action",
      className: "ps-4",
      render: (item) => (
        <div
          className="cursor-pointer text-muted hover-primary"
          onClick={() => {
            setSelectedBeneficiary(item);
            setShowDetailsModal(true);
          }}
        >
          <i className="far fa-eye text-secondary fs-16"></i>
        </div>
      ),
    },
  ];

  const handleSearch = () => {
    let result = beneficiariesData;
    if (userId) {
      result = result.filter((item) => item.userId.includes(userId));
    }
    if (bnfName) {
      result = result.filter((item) =>
        item.beneficiaryName.toLowerCase().includes(bnfName.toLowerCase()),
      );
    }
    if (receiverType) {
      result = result.filter(
        (item) => item.receiverType === receiverType.value,
      );
    }
    // Add date filtering logic if needed
    setFilteredData(result);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleReset = () => {
    setUserId("");
    setBnfName("");
    setReceiverType(null);
    setFromDate(null);
    setToDate(null);
    setFilteredData(beneficiariesData);
    setCurrentPage(1);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold text-black mb-0">Beneficiaries</h2>
      </div>

      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Body className="p-4 pb-1">
          <Row className="g-3 mb-3">
            <Col xl={3} lg={4} md={6}>
              <CommonDatePicker
                label="From date"
                selected={fromDate}
                onChange={(date) => setFromDate(date)}
                placeholderText="Select from date"
              />
            </Col>
            <Col xl={3} lg={4} md={6}>
              <CommonDatePicker
                label="To date"
                selected={toDate}
                onChange={(date) => setToDate(date)}
                placeholderText="Select to date"
              />
            </Col>
            <Col lg={4} md={6}>
              <CommonSelect
                label="Type"
                options={receiverTypeOptions}
                value={receiverType}
                onChange={(v) => setReceiverType(v)}
                placeholder="Select type"
              />
            </Col>
          </Row>
          <Row className="g-3 mb-4 align-items-end">
            <Col xl={3} lg={4} md={6}>
              <CommonInput
                label="User ID"
                placeholder="Enter user ID"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
              />
            </Col>
            <Col xl={3} lg={4} md={6}>
              <CommonInput
                label="Beneficiary name"
                placeholder="Enter beneficiary name"
                value={bnfName}
                onChange={(e) => setBnfName(e.target.value)}
              />
            </Col>
            <Col xl={3} lg={12} className="d-flex gap-2">
              <CommonButton
                variant="outline"
                onClick={handleReset}
                className="p-0 d-flex align-items-center justify-content-center"
                style={{ width: "50px", height: "50px", minWidth: "50px" }}
              >
                <i className="flaticon-381-repeat-1"></i>
              </CommonButton>
              <CommonButton
                onClick={handleSearch}
                className="p-0 d-flex align-items-center justify-content-center"
                style={{ width: "50px", height: "50px", minWidth: "50px" }}
              >
                <i className="flaticon-381-search-1 fs-20"></i>
              </CommonButton>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
        <div className="card-body">
          <DataTable
            columns={columns}
            data={currentItems}
            totalItems={filteredData.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            paginate={paginate}
            minWidth="100%"
          />
        </div>
      </div>

      <BeneficiaryDetailsModal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        beneficiary={selectedBeneficiary}
      />

      <style>{`
        .fs-14 { font-size: 14px; }
        .fs-18 { font-size: 18px; }
        .hover-primary:hover { color: var(--primary) !important; }
        .cursor-pointer { cursor: pointer; }
      `}</style>
    </div>
  );
};

export default Beneficiaries;
